import { useCallback } from "react";
import classNames from "classnames";
import findIndex from "lodash/findIndex";

import inputConnect from "../inputConnect";
import MatrixInputDesktopInterface from "./MatrixInputDesktopInterface";
import { Desktop, NonDesktop } from "../../Utils/ResponsiveQueries";
import MatrixInputNonDesktopInterface from "./MatrixInputNonDesktopInterface";

const MatrixInput = props => {
  const {
    values,
    updateValue,
    fieldName,
    singleOptionSelection,
    other,
    otherValue,
    items,
    options
  } = props;

  // This method allows searching for an option value index in the values array, by
  // passing an object as input to search an existing object with. It's used either to
  // search an object by itemId only, or by itemId and optionId.
  const optionValueIndex = useCallback(
    (optionValue, valuesSource = values) => findIndex(valuesSource, optionValue),
    [values]
  );

  const isChecked = useCallback(
    optionValue => optionValueIndex(optionValue) >= 0,
    [optionValueIndex]
  );

  const update = useCallback(
    data => {
      const currentData = { values, other, otherValue };

      updateValue({ ...currentData, ...data });
    },
    [other, otherValue, updateValue, values]
  );

  const removeOptionValue = useCallback(
    (optionValue, valuesSource) => {
      const targetValueIdentifierIndex = optionValueIndex(optionValue, valuesSource);
      if (targetValueIdentifierIndex < 0) return;

      valuesSource.splice(targetValueIdentifierIndex, 1);
    },
    [optionValueIndex]
  );

  const handleOptionChanged = useCallback(
    e => {
      const { value, checked } = e.target;
      const optionValue = JSON.parse(value);
      const wasChecked = isChecked(optionValue);
      const newValues = [...values];

      if (checked && !wasChecked) {
        // If we only allow one option to be selected pet item, we need to remove the
        // previous option value for the same item.
        if (singleOptionSelection) {
          removeOptionValue({ itemId: optionValue.itemId }, newValues);
        }

        newValues.push(optionValue);
      } else if (!checked && wasChecked) {
        removeOptionValue(optionValue, newValues);
      }

      update({ values: newValues });
    },
    [isChecked, removeOptionValue, singleOptionSelection, update, values]
  );

  const maximumSelectableOptions = singleOptionSelection
    ? 1
    : props.maximumSelectableOptions || options.length;

  const className = classNames("matrix-input", props.className);

  return (
    <div className={className}>
      <Desktop>
        <MatrixInputDesktopInterface
          fieldName={fieldName}
          items={items}
          options={options}
          isChecked={isChecked}
          onChange={handleOptionChanged}
          maximumSelectableOptions={maximumSelectableOptions}
          singleOptionSelection={singleOptionSelection}
        />
      </Desktop>

      <NonDesktop>
        <MatrixInputNonDesktopInterface
          fieldName={fieldName}
          items={items}
          options={options}
          isChecked={isChecked}
          onChange={handleOptionChanged}
          maximumSelectableOptions={maximumSelectableOptions}
          singleOptionSelection={singleOptionSelection}
        />
      </NonDesktop>
    </div>
  );
};

export default inputConnect(MatrixInput, {
  fields: { values: [], other: false, otherValue: "" }
});
